import React, { useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { opportunityPropType } from 'constants/propTypes';
import { bem } from 'helpers/class-name';

import { Title } from 'features/App';
import SmartSpotBanner from 'features/SmartSpotBanner';
import Button from 'components/Button';
import FeatureNotSupported from 'components/FeatureNotSupported';
import InfoBanner from 'components/InfoBanner/InfoBanner';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import Tabs, { Tab, TabLabel } from 'components/Tabs';
import When from 'components/When';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { makePath } from 'helpers/link-generation';

import { SpotRow, ClassRow } from './Row';
import OpportunitiesEmpty from './Empty';

import { messages, integrationsBannerMessages } from './messages';

const { block, element } = bem('opportunities');

const Opportunities = ({
  actOnOpportunities,
  actOnOpportunity,
  actOnSmartSpotOpportunities,
  classOpportunities,
  featureIsSupported,
  hasSpotChangeOpportunities,
  isLoaded,
  isManualStudio,
  isMsInventoryVenue,
  opportunities,
  spotOpportunities,
}) => {
  const navigate = useNavigate();

  const OPPORTUNTIES_UPDATE = 'opportunities/opportunities/OPPORTUNITIES_UPDATE';
  const SET_SPOTS_TO_ADD = 'opportunities/opportunities/SET_SPOTS_TO_ADD';
  const TOGGLE_IS_RERCURRING = 'opportunities/opportunities/SET_IS_RECURRING';

  const init = (updatedOpportunities, state = {}) =>
    updatedOpportunities.reduce(
      (memo, opp) =>
        Object.assign(memo, {
          [opp.scheduleId]: {
            spotsToAdd: state[opp.scheduleId] ? state[opp.scheduleId].spotsToAdd : opp.recommendation,
            isRecurring: state[opp.scheduleId] ? state[opp.scheduleId].isRecurring : false,
          },
        }),
      {}
    );

  const reducer = (state, action) => {
    const { type, updatedOpportunities, scheduleId, spotsToAdd } = action;
    switch (type) {
      case OPPORTUNTIES_UPDATE:
        return init(updatedOpportunities, state);
      case SET_SPOTS_TO_ADD:
        return {
          ...state,
          [scheduleId]: {
            ...state[scheduleId],
            spotsToAdd,
          },
        };
      case TOGGLE_IS_RERCURRING:
        return {
          ...state,
          [scheduleId]: {
            ...state[scheduleId],
            isRecurring: !state[scheduleId].isRecurring,
          },
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, opportunities, init);

  useEffect(() => {
    dispatch({ type: OPPORTUNTIES_UPDATE, updatedOpportunities: opportunities });
  }, [opportunities]);

  const setSpotsToAdd = (scheduleId, spotsToAdd) => {
    dispatch({ type: SET_SPOTS_TO_ADD, scheduleId, spotsToAdd });
  };

  const toggleIsRecurring = scheduleId => {
    dispatch({ type: TOGGLE_IS_RERCURRING, scheduleId });
  };

  const handleAcceptOpportunity = (opportunity, spotsAdded, isRecurring) => {
    const { scheduleId, startTime, className, opportunitySource, totalClasspassCapacity } = opportunity;

    if (opportunitySource === 'SMARTSPOT') {
      const smartSpotUpdate = {
        scheduleId,
        spots: totalClasspassCapacity + spotsAdded,
        spotsAdded,
        ssEnabled: true,
        withAllFutures: false,
        className,
        startTime,
      };
      actOnSmartSpotOpportunities([smartSpotUpdate]);
    } else {
      actOnOpportunity(
        scheduleId,
        spotsAdded,
        className,
        startTime,
        opportunitySource,
        totalClasspassCapacity,
        isRecurring
      );
    }

    braze.trackEvent(BRAZE_CUSTOM_EVENTS.OPPORTUNITY_ACCEPT);
  };

  const handleAcceptAllOpportunities = () => {
    const smartSpotOpportunities = opportunities.filter(
      ({ classDisabled, opportunitySource }) => !classDisabled && opportunitySource === 'SMARTSPOT'
    );
    const traditionalOpportunities = opportunities.filter(
      ({ classDisabled, opportunitySource }) => !classDisabled && opportunitySource !== 'SMARTSPOT'
    );

    if (smartSpotOpportunities.length > 0) {
      const smartSpotUpdates = smartSpotOpportunities.map(({ scheduleId, totalClasspassCapacity }) => ({
        scheduleId,
        spots: totalClasspassCapacity + state[scheduleId].spotsToAdd,
        spotsAdded: state[scheduleId].spotsToAdd,
        ssEnabled: true,
        withAllFutures: false,
      }));
      actOnSmartSpotOpportunities(smartSpotUpdates);
    }

    if (traditionalOpportunities.length > 0) {
      const actedOnOpportunities = traditionalOpportunities.map(({ scheduleId }) => [
        scheduleId,
        state[scheduleId].spotsToAdd,
        state[scheduleId].isRecurring,
      ]);
      actOnOpportunities(actedOnOpportunities);
    }

    braze.trackEvent(BRAZE_CUSTOM_EVENTS.OPPORTUNITY_ALL_ACCEPT);
  };

  const renderSpotList = (tableOpportunities, RowComponent) => {
    if (!tableOpportunities.length) {
      return <OpportunitiesEmpty />;
    }

    return tableOpportunities.map(opportunity => (
      <RowComponent
        key={opportunity.scheduleId}
        opportunity={opportunity}
        spotsToAdd={
          state[opportunity.scheduleId] ? state[opportunity.scheduleId].spotsToAdd : opportunity.recommendation
        }
        isRecurring={state[opportunity.scheduleId] ? state[opportunity.scheduleId].isRecurring : false}
        isManualStudio={isManualStudio}
        onAcceptOpportunity={handleAcceptOpportunity}
        setSpotsToAdd={setSpotsToAdd}
        toggleIsRecurring={toggleIsRecurring}
        // Pass opportunity source to row components for SmartSpot-specific behavior
        opportunitySource={opportunity.opportunitySource}
      />
    ));
  };

  const renderRecommendations = () =>
    hasSpotChangeOpportunities ? (
      <div className={element('recommendations')}>
        <Message as={Button} onClick={handleAcceptAllOpportunities} styled="primary" message={messages.acceptAll} />
      </div>
    ) : null;

  return (
    <div className={block()}>
      <Message message={messages.title} as={Title} />

      {!featureIsSupported ? (
        <FeatureNotSupported />
      ) : (
        <div className={element('container')}>
          <SmartSpotBanner className={element('smart-spot-banner')} />
          <When predicate={isMsInventoryVenue}>
            <InfoBanner
              callToAction={integrationsBannerMessages.ctaButton}
              icon={null}
              onClick={() => navigate(makePath.integrations())}
              subtitle={integrationsBannerMessages.subtitle}
              title={integrationsBannerMessages.title}
              wideButton
            />
          </When>
          <Tabs tabListProps={{ className: element('tab-list', 'mb-l') }}>
            <Tab name="add-spots">
              <TabLabel>
                <Message message={messages.addSpotsTabTitle} />
              </TabLabel>
              <div className={element('description', 'mh-s lg-mh-0')}>
                <Message as="h5" className="mb-small" message={messages.spotsTitle} />
                <Message className={element('description-biline', 'mb-r')} as="p" message={messages.spotsBiline} />
                {spotOpportunities && spotOpportunities.length > 0 && renderRecommendations()}
              </div>
              {isLoaded ? (
                <div className={element('table')}>{renderSpotList(spotOpportunities, SpotRow)}</div>
              ) : (
                <LoadingDots />
              )}
            </Tab>

            <Tab name="enable-classes">
              <TabLabel>
                <Message message={messages.enableClassesTabTitle} />
              </TabLabel>
              <div className={element('description', 'mh-s lg-mh-0')}>
                <Message as="h5" className="mb-small" message={messages.classesTitle} />
                <Message className={element('description-biline', 'mb-l')} as="p" message={messages.classesBiline} />
              </div>
              {isLoaded ? (
                <div className={element('table')}>{renderSpotList(classOpportunities, ClassRow)}</div>
              ) : (
                <LoadingDots />
              )}
            </Tab>
          </Tabs>
        </div>
      )}
    </div>
  );
};

Opportunities.propTypes = {
  actOnOpportunities: PropTypes.func,
  actOnOpportunity: PropTypes.func,
  actOnSmartSpotOpportunities: PropTypes.func,
  classOpportunities: PropTypes.arrayOf(opportunityPropType),
  featureIsSupported: PropTypes.bool,
  hasSpotChangeOpportunities: PropTypes.bool,
  isLoaded: PropTypes.bool,
  isManualStudio: PropTypes.bool,
  isMsInventoryVenue: PropTypes.bool,
  opportunities: PropTypes.arrayOf(opportunityPropType),
  spotOpportunities: PropTypes.arrayOf(opportunityPropType),
};

export default Opportunities;
